import { useState, useEffect, useRef } from "react";
import { getPreviewColorHex } from "../utils/colorPreviewHex";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import MaskAndValid from "utils/maskAndValid";
import {
  getDefaultCondicaoPagamentoId,
  resolveBulkPaymentIdFromFamilyCart,
  resolveLineDiscount,
  netUnitPrice,
} from "../utils/paymentConditionUtils";

function isColorSizeMatrix(fam) {
  return (
    fam?.matrixMode === "colorSize" &&
    Array.isArray(fam.matrixSizes) &&
    fam.matrixSizes.length > 0 &&
    Array.isArray(fam.matrixColors) &&
    fam.matrixColors.length > 0
  );
}

function sortProductsByPriceAsc(products, netPriceFor) {
  return [...(products || [])].sort((a, b) => netPriceFor(a) - netPriceFor(b));
}

function minNetPriceForSize(fam, size, netPriceFor) {
  const colors = fam.matrixColors || [];
  let min = Number.POSITIVE_INFINITY;
  colors.forEach((c) => {
    const p = fam.productByColorSize?.[`${c}|||${size}`];
    if (p) min = Math.min(min, netPriceFor(p));
  });
  return min;
}

function minNetPriceForColor(fam, color, netPriceFor) {
  const sizes = fam.matrixSizes || [];
  let min = Number.POSITIVE_INFINITY;
  sizes.forEach((s) => {
    const p = fam.productByColorSize?.[`${color}|||${s}`];
    if (p) min = Math.min(min, netPriceFor(p));
  });
  return min;
}

function sortMatrixSizesByPriceAsc(fam, netPriceFor) {
  return [...(fam.matrixSizes || [])].sort(
    (a, b) =>
      minNetPriceForSize(fam, a, netPriceFor) -
      minNetPriceForSize(fam, b, netPriceFor),
  );
}

function sortMatrixColorsByPriceAsc(fam, netPriceFor) {
  return [...(fam.matrixColors || [])].sort(
    (a, b) =>
      minNetPriceForColor(fam, a, netPriceFor) -
      minNetPriceForColor(fam, b, netPriceFor),
  );
}

function minNetPriceForVariantLegacy(fam, variant, netPriceFor) {
  let min = Number.POSITIVE_INFINITY;
  (fam.products || []).forEach((p) => {
    if (p.corLabel && p.corLabel !== "Geral" && p.corLabel !== variant) return;
    min = Math.min(min, netPriceFor(p));
  });
  return min;
}

function sortVariantsByPriceAsc(fam, netPriceFor) {
  return [...(fam.variants || [])].sort(
    (a, b) =>
      minNetPriceForVariantLegacy(fam, a, netPriceFor) -
      minNetPriceForVariantLegacy(fam, b, netPriceFor),
  );
}

function truncateToolbarLabel(text, maxLen = 20) {
  const value = text == null ? "" : String(text).trim();
  if (!value) return "—";
  if (value.length <= maxLen) return value;
  return `${value.substring(0, maxLen - 2)}...`;
}

function orderKeyBelongsToFamily(orderKey, familyKey) {
  if (!familyKey || !orderKey) return false;
  const prefix = `${familyKey}_`;
  return orderKey === familyKey || orderKey.startsWith(prefix);
}

function MatrixSizeColumnHeader({ text, className, style }) {
  const raw = text == null ? "" : String(text);
  if (!raw.includes(";")) {
    return (
      <Typography className={className} style={style}>
        {raw.trim()}
      </Typography>
    );
  }
  const parts = raw
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <Typography className={className} style={{ ...style, lineHeight: 1.45 }}>
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 ? <br /> : null}
          {part}
        </span>
      ))}
    </Typography>
  );
}

function getRepresentativeProductForSizeColumn(fam, size) {
  const colors = fam.matrixColors || [];
  for (let i = 0; i < colors.length; i++) {
    const p = fam.productByColorSize?.[`${colors[i]}|||${size}`];
    if (p) return p;
  }
  return null;
}

function BulkToolbarPaymentCondition({
  condicoes,
  value,
  onChange,
  defaultId,
  theme,
  dropdownOpen,
  setDropdownOpen,
  hideLeadingDivider = false,
}) {
  if (!condicoes || condicoes.length === 0) return null;

  const resolvedId =
    value ??
    getDefaultCondicaoPagamentoId(condicoes, defaultId) ??
    condicoes[0]?.idCondicaoPagamento;
  const selected = condicoes.find((c) => c.idCondicaoPagamento === resolvedId);
  const label = selected?.label || "Condição de pagamento";

  if (condicoes.length === 1) {
    return (
      <>
        {!hideLeadingDivider && <Box className="toolbar-divider" />}
        <Box
          className="custom-select-wrapper"
          style={{ minWidth: 200, maxWidth: 360, flex: 1 }}
        >
          <Box
            className="custom-select-btn"
            style={{ cursor: "default", pointerEvents: "none" }}
          >
            <Typography
              noWrap
              style={{ fontSize: 14, flex: 1 }}
              title={condicoes[0].label}
            >
              {condicoes[0].label}
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      {!hideLeadingDivider && <Box className="toolbar-divider" />}
      <Box
        className="custom-select-wrapper"
        style={{ minWidth: 220, maxWidth: 420, flex: 1 }}
      >
        <Box
          className="custom-select-btn"
          onClick={() => setDropdownOpen(!dropdownOpen)}
          style={{
            borderColor: dropdownOpen ? theme.primary : undefined,
            boxShadow: dropdownOpen ? `0 0 0 1px ${theme.primary}` : undefined,
          }}
        >
          <Typography
            noWrap
            style={{ fontSize: 14, flex: 1, marginRight: 8 }}
            title={label}
          >
            {label}
          </Typography>
          <ExpandMoreIcon />
        </Box>
        {dropdownOpen && (
          <Box className="custom-select-list">
            {condicoes.map((c) => (
              <Box
                key={c.idCondicaoPagamento}
                className={`custom-select-item ${
                  resolvedId === c.idCondicaoPagamento ? "selected" : ""
                }`}
                onClick={() => {
                  onChange(c.idCondicaoPagamento);
                  setDropdownOpen(false);
                }}
                style={
                  resolvedId === c.idCondicaoPagamento
                    ? {
                        backgroundColor: "#eff6ff",
                        color: theme.primary,
                      }
                    : undefined
                }
              >
                <Typography style={{ fontSize: 13 }}>{c.label}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}

function ColorSizeMatrixColumnHeader({ fam, size, theme, discountPercent }) {
  const rep = getRepresentativeProductForSizeColumn(fam, size);
  const disc = Number(discountPercent) || 0;
  const hasDiscount = rep && disc > 0;
  const finalPrice = rep ? netUnitPrice(rep, disc) : 0;

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 6,
        padding: "6px 4px",
        maxWidth: "fit-content",
        margin: "0 auto",
      }}
    >
      <MatrixSizeColumnHeader
        text={size}
        className="matrix-prod-name"
        style={{
          fontWeight: 600,
          fontSize: 12,
          color: "#374151",
        }}
      />
      {rep && (
        <>
          <Typography className="matrix-prod-ref">Ref: {rep.ref}</Typography>
          {hasDiscount ? (
            <Box className="matrix-price-container">
              <Typography className="matrix-price-old">
                R$ {MaskAndValid.maskDecimal(rep.price, 0)}
              </Typography>
              <Typography className="matrix-price-new">
                R$ {MaskAndValid.maskDecimal(finalPrice, 0)}
              </Typography>
              <Box className="matrix-discount-badge">-{disc}%</Box>
            </Box>
          ) : (
            <Typography
              className="matrix-price"
              style={{ color: theme.primary }}
            >
              R$ {MaskAndValid.maskDecimal(rep.price, 0)}
            </Typography>
          )}
        </>
      )}
      {!rep && (
        <Typography variant="caption" color="textSecondary">
          —
        </Typography>
      )}
    </Box>
  );
}

export default function GridModal({
  open,
  onClose,
  onSave,
  currentBrand,
  activeFamilyKey,
  families,
  tempGridState,
  setTempGridState,
  tempPaymentConditionState,
  setTempPaymentConditionState,
  selectedBulkColors,
  selectedBulkProducts,
  toggleBulkSelection,
  theme,
}) {
  const [bulkQty, setBulkQty] = useState("");
  const [bulkPaymentConditionId, setBulkPaymentConditionId] = useState(null);
  const [colorDropdownOpen, setColorDropdownOpen] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  const [selectedMatrixCells, setSelectedMatrixCells] = useState(new Set());
  const gridModalSessionKeyRef = useRef(null);

  useEffect(() => {
    if (!open) {
      gridModalSessionKeyRef.current = null;
      return;
    }

    if (!activeFamilyKey || !families[currentBrand]?.[activeFamilyKey]) return;

    const fam = families[currentBrand][activeFamilyKey];
    const sessionKey = `${currentBrand}:${activeFamilyKey}`;
    const isNewSession = gridModalSessionKeyRef.current !== sessionKey;
    gridModalSessionKeyRef.current = sessionKey;

    if (!isNewSession) return;

    const allCells = new Set();
    if (isColorSizeMatrix(fam)) {
      fam.matrixColors.forEach((color) => {
        fam.matrixSizes.forEach((size) => {
          const prod = fam.productByColorSize?.[`${color}|||${size}`];
          if (prod) {
            allCells.add(`${activeFamilyKey}_${prod.id}`);
          }
        });
      });
    } else {
      fam.variants.forEach((variant) => {
        fam.products.forEach((prod) => {
          const compoundId = `${activeFamilyKey}_${prod.id}_${variant}`;
          allCells.add(compoundId);
        });
      });
    }
    setSelectedMatrixCells(allCells);

    const condicoes = fam.condicoesPagamento || [];
    const initialBulkId = resolveBulkPaymentIdFromFamilyCart(
      activeFamilyKey,
      tempGridState,
      tempPaymentConditionState,
      condicoes,
      fam.idCondicaoPagamentoPadrao,
    );
    setBulkPaymentConditionId(initialBulkId);

    if (initialBulkId != null && condicoes.length > 0) {
      setTempPaymentConditionState((prev) => {
        const next = { ...prev };
        let changed = false;
        Object.entries(tempGridState).forEach(([key, qty]) => {
          if (!orderKeyBelongsToFamily(key, activeFamilyKey)) return;
          if (!qty || Number(qty) <= 0) return;
          if (next[key] == null || next[key] === "") {
            next[key] = initialBulkId;
            changed = true;
          }
        });
        return changed ? next : prev;
      });
    }
  }, [open, activeFamilyKey, currentBrand, families, setTempPaymentConditionState]);

  if (!activeFamilyKey) return null;

  const fam = families[currentBrand][activeFamilyKey];
  const condicoesPagamento = fam.condicoesPagamento || [];
  const defaultCondicaoId = fam.idCondicaoPagamentoPadrao;
  const isMatrixColorSize = isColorSizeMatrix(fam);
  const bulkSecondaryKind = isMatrixColorSize ? "tamanho" : "variedade";
  const belongsToActiveFamily = (orderKey) =>
    orderKeyBelongsToFamily(orderKey, activeFamilyKey);

  const applyPaymentForCell = (compoundId, nextPaymentState, qty) => {
    const next = { ...nextPaymentState };
    if (qty <= 0) {
      delete next[compoundId];
      return next;
    }
    if (condicoesPagamento.length === 0) return next;
    const id =
      bulkPaymentConditionId ??
      getDefaultCondicaoPagamentoId(condicoesPagamento, defaultCondicaoId);
    if (id == null) return next;
    next[compoundId] = id;
    return next;
  };

  const resolvePaymentIdForCell = (compoundId) => {
    if (compoundId) {
      return (
        tempPaymentConditionState[compoundId] ??
        bulkPaymentConditionId ??
        defaultCondicaoId
      );
    }
    return bulkPaymentConditionId ?? defaultCondicaoId;
  };

  const discountForProd = (prod, compoundId) =>
    resolveLineDiscount(prod, fam, resolvePaymentIdForCell(compoundId));

  const netPriceFor = (prod, compoundId) => {
    if (!prod) return Number.POSITIVE_INFINITY;
    return netUnitPrice(prod, discountForProd(prod, compoundId));
  };

  const handleBulkPaymentChange = (id) => {
    const normalizedId = Number(id);
    if (!normalizedId || Number.isNaN(normalizedId)) return;

    setBulkPaymentConditionId(normalizedId);
    setTempPaymentConditionState((prev) => {
      const next = { ...prev };
      Object.entries(tempGridState).forEach(([key, qty]) => {
        if (!belongsToActiveFamily(key) || !qty || Number(qty) <= 0) return;
        next[key] = normalizedId;
      });
      return next;
    });
  };

  const updateQtyForCell = (compoundId, rawValue, multiple) => {
    let intVal = parseInt(rawValue, 10);
    const newTempState = { ...tempGridState };
    let newPayment = { ...tempPaymentConditionState };

    if (intVal > 0) {
      const remainder = intVal % multiple;
      if (remainder !== 0) {
        intVal = intVal + (multiple - remainder);
      }
      newTempState[compoundId] = intVal;
      newPayment = applyPaymentForCell(compoundId, newPayment, intVal);
    } else {
      delete newTempState[compoundId];
      delete newPayment[compoundId];
    }

    setTempGridState(newTempState);
    setTempPaymentConditionState(newPayment);
  };

  const productsSorted = sortProductsByPriceAsc(fam.products, (p) =>
    netPriceFor(p, `${activeFamilyKey}_${p.id}`),
  );
  let matrixSizesSorted = fam.matrixSizes || [];
  let matrixColorsSorted = fam.matrixColors || [];
  let variantsSorted = fam.variants || [];
  const netPriceForBulk = (prod) => netPriceFor(prod, null);
  if (isColorSizeMatrix(fam)) {
    matrixSizesSorted = sortMatrixSizesByPriceAsc(fam, netPriceForBulk);
    matrixColorsSorted = sortMatrixColorsByPriceAsc(fam, netPriceForBulk);
    variantsSorted = matrixColorsSorted;
  } else {
    variantsSorted = sortVariantsByPriceAsc(fam, netPriceForBulk);
  }

  const toggleMatrixCheckbox = (compoundId) => {
    const newSet = new Set(selectedMatrixCells);
    if (newSet.has(compoundId)) {
      newSet.delete(compoundId);
    } else {
      newSet.add(compoundId);
    }
    setSelectedMatrixCells(newSet);
  };

  const handleConfirmSave = () => {
    const resolvedPaymentId =
      bulkPaymentConditionId ??
      getDefaultCondicaoPagamentoId(condicoesPagamento, defaultCondicaoId);

    const finalGrid = { ...tempGridState };
    const finalPayment = {};

    Object.entries(finalGrid).forEach(([key, qty]) => {
      if (qty == null || Number(qty) <= 0) {
        delete finalGrid[key];
        return;
      }
      if (
        belongsToActiveFamily(key) &&
        condicoesPagamento.length > 0 &&
        resolvedPaymentId != null
      ) {
        finalPayment[key] = resolvedPaymentId;
      }
    });

    onSave(finalGrid, finalPayment);
  };

  const handleApplyBulk = () => {
    const qty = parseInt(bulkQty, 10);
    if (!isNaN(qty) && qty >= 0) {
      setColorDropdownOpen(false);
      setProductDropdownOpen(false);
      setPaymentDropdownOpen(false);

      const newTempState = { ...tempGridState };
      const newPaymentState = { ...tempPaymentConditionState };

      if (isColorSizeMatrix(fam)) {
        fam.matrixColors.forEach((color) => {
          const colorMatch =
            selectedBulkColors.size === 0 || selectedBulkColors.has(color);
          if (!colorMatch) return;
          fam.matrixSizes.forEach((size) => {
            const prod = fam.productByColorSize?.[`${color}|||${size}`];
            if (!prod) return;
            const sizeMatch =
              selectedBulkProducts.size === 0 || selectedBulkProducts.has(size);
            const compoundId = `${activeFamilyKey}_${prod.id}`;
            const isCheckboxSelected = selectedMatrixCells.has(compoundId);
            if (sizeMatch && isCheckboxSelected) {
              const multiple = prod.multiple || 1;
              let adjustedQty = qty;
              if (adjustedQty > 0) {
                const remainder = adjustedQty % multiple;
                if (remainder !== 0) {
                  adjustedQty = adjustedQty + (multiple - remainder);
                }
              }
              if (adjustedQty > 0) {
                newTempState[compoundId] = adjustedQty;
              } else {
                delete newTempState[compoundId];
                delete newPaymentState[compoundId];
              }
            }
          });
        });
      } else {
        fam.variants.forEach((variant) => {
          fam.products.forEach((prod) => {
            if (
              prod.corLabel &&
              prod.corLabel !== "Geral" &&
              prod.corLabel !== variant
            ) {
              return;
            }
            const compoundId = `${activeFamilyKey}_${prod.id}_${variant}`;
            const colorMatch =
              selectedBulkColors.size === 0 || selectedBulkColors.has(variant);
            const productMatch =
              selectedBulkProducts.size === 0 ||
              selectedBulkProducts.has(prod.id);
            const isCheckboxSelected = selectedMatrixCells.has(compoundId);

            if (colorMatch && productMatch && isCheckboxSelected) {
              const multiple = prod.multiple || 1;
              let adjustedQty = qty;
              if (adjustedQty > 0) {
                const remainder = adjustedQty % multiple;
                if (remainder !== 0) {
                  adjustedQty = adjustedQty + (multiple - remainder);
                }
              }
              if (adjustedQty > 0) {
                newTempState[compoundId] = adjustedQty;
              } else {
                delete newTempState[compoundId];
                delete newPaymentState[compoundId];
              }
            }
          });
        });
      }

      setTempGridState(newTempState);
      setTempPaymentConditionState(newPaymentState);
      setBulkQty("");
    }
  };

  const bulkColorOptions = isMatrixColorSize
    ? matrixColorsSorted
    : variantsSorted;

  const bulkSecondaryOptions = isMatrixColorSize
    ? matrixSizesSorted.map((size) => ({ id: size, label: size }))
    : productsSorted.map((p) => ({ id: p.id, label: p.name }));

  const getColorLabel = () => {
    if (selectedBulkColors.size === 0) return "Todas as cores";
    if (selectedBulkColors.size === 1) {
      return truncateToolbarLabel(Array.from(selectedBulkColors)[0]);
    }
    return `${selectedBulkColors.size} cores`;
  };

  const getSecondaryBulkLabel = () => {
    const allLabel = isMatrixColorSize
      ? "Todos os tamanhos"
      : "Todas as variedades";
    if (selectedBulkProducts.size === 0) return allLabel;
    if (selectedBulkProducts.size === 1) {
      const key = Array.from(selectedBulkProducts)[0];
      if (isMatrixColorSize) {
        return truncateToolbarLabel(key);
      }
      const prod = productsSorted.find((p) => p.id === key);
      return truncateToolbarLabel(prod ? prod.name : key);
    }
    const unit = isMatrixColorSize ? "tamanhos" : "variedades";
    return `${selectedBulkProducts.size} ${unit}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      className="grid-modal-dialog"
      PaperProps={{
        className: "grid-modal-paper",
      }}
    >
      <DialogTitle className="grid-modal-header">
        <Box className="grid-modal-header-left">
          <Box>
            <Typography
              className="grid-modal-title brand-font"
              style={{ color: theme.primary }}
            >
              {fam.productDisplayName || fam.name}
            </Typography>
            <Typography className="grid-modal-subtitle">
              {condicoesPagamento.length > 0
                ? "Use Massa para quantidade; a condição de pagamento fica na faixa abaixo"
                : "Selecione variantes"}
            </Typography>
          </Box>
          <Button onClick={onClose} className="close-btn">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <Box className="grid-modal-toolbar-stack">
        <Box className="grid-modal-toolbar grid-modal-toolbar-massa">
          <Typography
            className="toolbar-label"
            style={{ color: theme.primary }}
          >
            Massa:
          </Typography>
          <Box
            className="bulk-qty-input-wrapper"
            onFocus={(e) => {
              const wrapper = e.currentTarget;
              if (wrapper) {
                wrapper.style.boxShadow = `0 0 0 1px ${theme.primary}`;
                wrapper.style.borderColor = theme.primary;
              }
            }}
            onBlur={(e) => {
              const wrapper = e.currentTarget;
              if (wrapper && !wrapper.contains(document.activeElement)) {
                wrapper.style.boxShadow = "0 1px 2px 0 rgba(0, 0, 0, 0.05)";
                wrapper.style.borderColor = "#d1d5db";
              }
            }}
          >
            <Typography className="bulk-qty-label">Qtd</Typography>
            <TextField
              type="number"
              value={bulkQty}
              onChange={(e) => setBulkQty(e.target.value)}
              placeholder="0"
              className="bulk-qty-input"
              variant="outlined"
              fullWidth
              InputProps={{
                className: "bulk-qty-input-inner",
                notched: false,
              }}
              inputProps={{
                style: { height: "100%", width: "80px" },
              }}
              style={{
                border: "none",
                background: "none",
                boxShadow: "none",
                height: "100%",
                width: "80px",
              }}
            />
          </Box>
          <Box className="toolbar-divider" />
          <Box className="custom-select-wrapper">
            <Box
              className="custom-select-btn"
              onClick={() => setColorDropdownOpen(!colorDropdownOpen)}
              style={{
                borderColor: colorDropdownOpen ? theme.primary : undefined,
                boxShadow: colorDropdownOpen
                  ? `0 0 0 1px ${theme.primary}`
                  : undefined,
              }}
            >
              <Typography>{getColorLabel()}</Typography>
              <ExpandMoreIcon />
            </Box>
            {colorDropdownOpen && (
              <Box className="custom-select-list">
                {bulkColorOptions.map((v) => (
                  <Box
                    key={v}
                    className={`custom-select-item ${
                      selectedBulkColors.has(v) ? "selected" : ""
                    }`}
                    onClick={() => toggleBulkSelection("color", v)}
                    style={
                      selectedBulkColors.has(v)
                        ? {
                            backgroundColor: "#eff6ff",
                            color: theme.primary,
                          }
                        : undefined
                    }
                  >
                    <Checkbox
                      checked={selectedBulkColors.has(v)}
                      className="b2b-checkbox"
                      style={{ color: theme.primary }}
                    />
                    <Typography>{v}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Box className="custom-select-wrapper">
            <Box
              className="custom-select-btn"
              onClick={() => setProductDropdownOpen(!productDropdownOpen)}
              style={{
                borderColor: productDropdownOpen ? theme.primary : undefined,
                boxShadow: productDropdownOpen
                  ? `0 0 0 1px ${theme.primary}`
                  : undefined,
              }}
            >
              <Typography>{getSecondaryBulkLabel()}</Typography>
              <ExpandMoreIcon />
            </Box>
            {productDropdownOpen && (
              <Box className="custom-select-list">
                {bulkSecondaryOptions.map((option) => (
                  <Box
                    key={option.id}
                    className={`custom-select-item ${
                      selectedBulkProducts.has(option.id) ? "selected" : ""
                    }`}
                    onClick={() => toggleBulkSelection("prod", option.id)}
                    style={
                      selectedBulkProducts.has(option.id)
                        ? {
                            backgroundColor: "#eff6ff",
                            color: theme.primary,
                          }
                        : undefined
                    }
                  >
                    <Checkbox
                      checked={selectedBulkProducts.has(option.id)}
                      className="b2b-checkbox"
                      style={{ color: theme.primary }}
                    />
                    <Typography>{option.label}</Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          <Button
            className="apply-bulk-btn"
            onClick={handleApplyBulk}
            style={{ backgroundColor: theme.primary }}
          >
            Aplicar
          </Button>

          <Box className="toolbar-info toolbar-info-massa">
            <InfoIcon className="info-icon" style={{ color: theme.primary }} />
            <Typography className="info-text">
              O botão Aplicar altera apenas a <strong>quantidade</strong> dos
              itens{" "}
              <Box
                component="span"
                className="checkmark-icon"
                style={{ backgroundColor: theme.primary }}
              >
                ✓
              </Box>{" "}
              marcados na grade.
            </Typography>
          </Box>
        </Box>

        {condicoesPagamento.length > 0 && (
          <Box className="grid-modal-toolbar grid-modal-toolbar-payment">
            <Typography
              className="toolbar-label"
              style={{ color: theme.primary }}
            >
              Condição de pagamento:
            </Typography>
            <BulkToolbarPaymentCondition
              condicoes={condicoesPagamento}
              value={bulkPaymentConditionId}
              onChange={handleBulkPaymentChange}
              defaultId={defaultCondicaoId}
              theme={theme}
              dropdownOpen={paymentDropdownOpen}
              setDropdownOpen={setPaymentDropdownOpen}
              hideLeadingDivider
            />
            <Box className="toolbar-info toolbar-info-payment">
              <InfoIcon
                className="info-icon"
                style={{ color: theme.primary }}
              />
              <Typography className="info-text">
                Válida para todos os itens com quantidade na grade. Ao{" "}
                <strong>Confirmar</strong>, esta condição será usada no pedido.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <DialogContent className="grid-modal-content">
        <Table className="matrix-table">
          {isColorSizeMatrix(fam) ? (
            <>
              <TableHead className="matrix-header">
                <TableRow>
                  <TableCell className="matrix-header-col sticky">
                    Cor / Tamanho
                  </TableCell>
                  {matrixSizesSorted.map((size) => {
                    const rep = getRepresentativeProductForSizeColumn(
                      fam,
                      size,
                    );
                    const headerDisc = rep
                      ? discountForProd(rep, `${activeFamilyKey}_${rep.id}`)
                      : resolveLineDiscount(
                          null,
                          fam,
                          bulkPaymentConditionId ?? defaultCondicaoId,
                        );
                    return (
                      <TableCell key={size} className="matrix-header-col">
                        <ColorSizeMatrixColumnHeader
                          fam={fam}
                          size={size}
                          theme={theme}
                          discountPercent={headerDisc}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrixColorsSorted.map((color) => (
                  <TableRow key={color} className="matrix-row">
                    <TableCell className="matrix-variant-cell sticky">
                      <Box className="variant-cell-content">
                        <Box
                          className="color-dot"
                          style={{
                            backgroundColor: getPreviewColorHex(color, fam),
                          }}
                        />
                        <Typography className="variant-name">
                          {color}
                        </Typography>
                      </Box>
                    </TableCell>
                    {matrixSizesSorted.map((size) => {
                      const prod =
                        fam.productByColorSize?.[`${color}|||${size}`];
                      if (!prod) {
                        return (
                          <TableCell key={size} className="matrix-input-cell">
                            <Typography variant="caption" color="textPrimary">
                              —
                            </Typography>
                          </TableCell>
                        );
                      }
                      const compoundId = `${activeFamilyKey}_${prod.id}`;
                      const val = tempGridState[compoundId] || "";
                      const multiple = prod.multiple || 1;
                      const multipleText =
                        multiple > 1 ? `Múlt: ${multiple}` : "-";
                      const isChecked = selectedMatrixCells.has(compoundId);

                      return (
                        <TableCell
                          key={`${color}-${size}`}
                          className="matrix-input-cell"
                        >
                          <Box
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <Checkbox
                              checked={isChecked}
                              onChange={() => toggleMatrixCheckbox(compoundId)}
                              className="matrix-checkbox b2b-checkbox"
                              style={{ color: theme.primary }}
                            />
                            <TextField
                              type="number"
                              value={val}
                              onChange={(e) =>
                                updateQtyForCell(
                                  compoundId,
                                  e.target.value,
                                  multiple,
                                )
                              }
                              onFocus={(e) => {
                                e.target.style.outline = `2px solid ${theme.primary}`;
                              }}
                              onBlur={(e) => {
                                e.target.style.outline = "none";
                              }}
                              placeholder={multipleText}
                              className="matrix-input"
                              min="0"
                              step={multiple}
                              InputProps={{
                                className: "matrix-input-inner",
                              }}
                            />
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </>
          ) : (
            <>
              <TableHead className="matrix-header">
                <TableRow>
                  <TableCell className="matrix-header-col sticky">
                    Cor / Variante
                  </TableCell>
                  {productsSorted.map((prod) => {
                    const prodDisc = discountForProd(
                      prod,
                      `${activeFamilyKey}_${prod.id}`,
                    );
                    const finalPrice = netUnitPrice(prod, prodDisc);
                    const hasDiscount = prodDisc > 0;

                    return (
                      <TableCell key={prod.id} className="matrix-header-col">
                        <Typography className="matrix-prod-name">
                          {prod.name}
                        </Typography>
                        <Typography className="matrix-prod-ref">
                          Ref: {prod.ref}
                        </Typography>
                        {hasDiscount ? (
                          <Box className="matrix-price-container">
                            <Typography className="matrix-price-old">
                              R$ {MaskAndValid.maskDecimal(prod.price, 0)}
                            </Typography>
                            <Typography className="matrix-price-new">
                              R$ {MaskAndValid.maskDecimal(finalPrice, 0)}
                            </Typography>
                            <Box className="matrix-discount-badge">
                              -{prodDisc}%
                            </Box>
                          </Box>
                        ) : (
                          <Typography
                            className="matrix-price"
                            style={{ color: theme.primary }}
                          >
                            R$ {MaskAndValid.maskDecimal(prod.price, 0)}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {variantsSorted.map((variant) => (
                  <TableRow key={variant} className="matrix-row">
                    <TableCell className="matrix-variant-cell sticky">
                      <Box className="variant-cell-content">
                        <Box
                          className="color-dot"
                          style={{
                            backgroundColor: getPreviewColorHex(variant, fam),
                          }}
                        />
                        <Typography className="variant-name">
                          {variant}
                        </Typography>
                      </Box>
                    </TableCell>
                    {productsSorted.map((prod) => {
                      const mismatch =
                        prod.corLabel &&
                        prod.corLabel !== "Geral" &&
                        prod.corLabel !== variant;
                      if (mismatch) {
                        return (
                          <TableCell
                            key={`${prod.id}-${variant}`}
                            className="matrix-input-cell"
                          >
                            <Typography variant="caption" color="textPrimary">
                              —
                            </Typography>
                          </TableCell>
                        );
                      }
                      const compoundId = `${activeFamilyKey}_${prod.id}_${variant}`;
                      const val = tempGridState[compoundId] || "";
                      const multiple = prod.multiple || 1;
                      const multipleText =
                        multiple > 1 ? `Múlt: ${multiple}` : "-";
                      const isChecked = selectedMatrixCells.has(compoundId);

                      return (
                        <TableCell key={prod.id} className="matrix-input-cell">
                          <Box className="matrix-input-wrapper">
                            <Checkbox
                              checked={isChecked}
                              onChange={() => toggleMatrixCheckbox(compoundId)}
                              className="matrix-checkbox b2b-checkbox"
                              style={{ color: theme.primary }}
                            />
                            <TextField
                              type="number"
                              value={val}
                              onChange={(e) =>
                                updateQtyForCell(
                                  compoundId,
                                  e.target.value,
                                  multiple,
                                )
                              }
                              onFocus={(e) => {
                                e.target.style.outline = `2px solid ${theme.primary}`;
                              }}
                              onBlur={(e) => {
                                e.target.style.outline = "none";
                              }}
                              placeholder={multipleText}
                              className="matrix-input"
                              min="0"
                              step={multiple}
                              InputProps={{
                                className: "matrix-input-inner",
                              }}
                            />
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      </DialogContent>

      <DialogActions className="grid-modal-actions">
        <Button onClick={onClose} className="cancel-btn">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirmSave}
          className="save-btn"
          style={{
            backgroundColor: theme.primary,
          }}
          startIcon={<SaveIcon />}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
